import { useCreateEvent } from "@/features/events/hooks/useCreateEvent"
import { EventType } from "@/features/events/types/EventType"
import FolderCard from "@/features/shared/components/FolderCard"
import { useNavigate } from "react-router-dom"
import { ClickableItem, HealthPlan, MealPlan, MealPlanData, MealPlanPdfType } from "../../../utils/api-objects/HealthPlan"
import { saveEvent } from "../../../utils/apis/user-service"
import { handleDate } from "../../../utils/Functions"
import { useUserStore } from "../../../utils/stores/useUserStore"
import { MealPlanDataBox, MealPlanDataBoxWithCheckbox } from "./MealPlanCard"

interface MealPlanSectionProps {
    mealPlan: MealPlan,
    healthPlan: HealthPlan | null,
}

const MealPlanSection = (props: MealPlanSectionProps) => {
    const { sendEvent } = useCreateEvent()
    const { userData } = useUserStore()
    const email = userData.email
    const navigate = useNavigate()

    const handleNavigate = async (pdfType: MealPlanPdfType) => {
        // navigate(`/health-plan/meal-plan/pdfs?meal_plan_id=${props.mealPlan.id}&type=${pdfType}`)
        navigate(`/meal-plan/pdfs?meal_plan_id=${props.mealPlan.id}&type=${pdfType}`)

        if (!email) return
        saveEvent(
            email,
            `click_on_${pdfType}_folder`,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "meal_plan_page",
        )
        await sendEvent({
            event: EventType.CLICK,
            sub_category: "open_health_plan_folder",
            event_metadata: { folder: pdfType }
        })
    }

    const pdfCounts = {
        [MealPlanPdfType.MealPlan]: props.mealPlan.pdf_data.filter(pdf => pdf.type === MealPlanPdfType.MealPlan).length,
        [MealPlanPdfType.SupportMaterials]: props.mealPlan.pdf_data.filter(pdf => pdf.type === MealPlanPdfType.SupportMaterials).length,
    }

    const dataBoxes = [
        { title: "👨‍⚕️ Commento del nutrizionista", key: "nutri_comment", Component: MealPlanDataBox },
        ...(props.healthPlan?.data.food_suggested ? [] : [
            { title: "🥙  Alimenti consigliati", key: "food_tips", Component: MealPlanDataBoxWithCheckbox },
        ]),
        // ✅ Only include if `props.healthPlan?.data.integrsatos_suggested` is **missing**
        ...(props.healthPlan?.data.integrators_suggested ? [] : [
            { title: "💊  Integratori consigliati", key: "integrators_suggested", Component: MealPlanDataBoxWithCheckbox },
        ]),
        { title: "🍲 Linee guida alimentari principali", key: "guidelines", Component: MealPlanDataBoxWithCheckbox },
        { title: "🍳 Ricette e accostamenti suggeriti", key: "recepees", Component: MealPlanDataBoxWithCheckbox },
        { title: "👨‍⚕️ Commento del nutrizionista", key: "nutrition_recommendations", Component: MealPlanDataBox },
        { title: "🥙 Alimenti consigliati", key: "top_hero_foods", Component: MealPlanDataBox },
        { title: "💊 Integratori consigliati", key: "top_integratori", Component: MealPlanDataBox },
        { title: "👨‍⚕️ Commento del nutrizionista", key: "comment", Component: MealPlanDataBox },
        { title: "💡 Cose importanti da tenere a mente", key: "important_key_points", Component: MealPlanDataBox },
    ]

    return (
        <div className="space-y-[20px] flex flex-col">
            <div className="font-normal text-[12px] leading-[33px]">
                Aggiornato il {handleDate(props.mealPlan.updated_at, "dd.MM.yyyy")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                {dataBoxes.map(({ title, key, Component }) => {
                    const data = props.mealPlan.data[key as keyof MealPlanData];

                    if (!data) return null;

                    const isClickableItemArray = (data: unknown): data is ClickableItem[] => {
                        return Array.isArray(data) && data.every(item => typeof item === "object" && "text" in item);
                    };

                    if (Component === MealPlanDataBoxWithCheckbox && isClickableItemArray(data)) {
                        return (
                            <Component
                                key={key}
                                title={title}
                                data={data}
                                fullData={props.mealPlan.data}
                                planId={props.mealPlan.id}
                                source="health_plan_page"
                            />
                        );
                    } else if (Component === MealPlanDataBox && Array.isArray(data)) {
                        return (
                            <Component
                                key={key}
                                title={title}
                                data={data as string[]}
                                source="health_plan_page"
                            />
                        );
                    } else {
                        return null;
                    }
                })}
            </div>
            {props.mealPlan.pdf_data.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                    {Object.entries(pdfCounts).map(([pdfType, count]) =>
                        count > 0 ? (
                            // <PdfFolder
                            //     key={pdfType}
                            //     title={pdfType === MealPlanPdfType.MealPlan ? "🥙 Dieta" : "🗂️ A supporto del Meal Plan"}
                            //     onClick={() => handleNavigate(pdfType as MealPlanPdfType)}
                            //     number={count}
                            // />
                            <FolderCard
                                key={pdfType}
                                onClick={() => handleNavigate(pdfType as MealPlanPdfType)} title={pdfType === MealPlanPdfType.MealPlan ? "🥙 Dieta" : "🗂️ A supporto del Meal Plan"}/>
                        ) : null
                    )}
                </div>
            )}
        </div>
    )
}

export default MealPlanSection
